import { KeyboardEvent, MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { cx } from '@emotion/css';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';

import { PhoenixIcon } from '../phoenix-icon/phoenix-icon';

export interface ChipProps {
  disabled?: boolean;
  children: string | [ReactNode, string];
  tabIndex?: number;
  className?: string;
  dataTestId?: string;
  checkIconWhenSelected?: boolean;
  icon?: string;
}

export interface ChipInputProps extends ChipProps {
  type: 'input';
  onDelete?: () => void;
}

export interface ChipFilterProps extends ChipProps {
  selected?: boolean;
  type: 'filter';
  onSelect?: (selected: boolean) => void;
}

export const Chip = ({
  children,
  type,
  disabled,
  tabIndex = 0,
  className = '',
  checkIconWhenSelected = true,
  dataTestId,
  icon,
  ...props
}: ChipFilterProps | ChipInputProps) => {
  const [isSelected, setIsSelected] = useState((props as ChipFilterProps).selected || false);
  useEffect(() => {
    setIsSelected((props as ChipFilterProps).selected || false);
  }, [(props as ChipFilterProps).selected]);

  const checkIcon = getDesignSystemIcon('icon-check-16.svg');
  const deleteIcon = getDesignSystemIcon('icon-cross-16.svg');
  const chipTabIndex = disabled ? -1 : tabIndex;
  const customNode = typeof children !== 'string' && children[0];

  const label = useMemo(() => {
    const labelValue = typeof children === 'string' ? children : children[1];
    return labelValue.length > 30 ? `${labelValue.substring(0, 28)}...` : labelValue;
  }, [children]);

  const commonClasses =
    'select-none outline-none inline-block grid grid-flow-col items-center space-x-2 h-8 max-w-xs px-4 border stihl-text-interactive-link-default rounded-3xl cursor-pointer';

  const renderSelected = () => {
    return (
      isSelected && (
        <svg className={`inline-block h-3 w-3 ${disabled ? 'text-secondary' : 'text-secondary-deep'} ${checkIcon}`}>
          <use xlinkHref={checkIcon + '#img'} />
        </svg>
      )
    );
  };

  const toggleSelect = () => {
    const newValue = !isSelected;
    (props as ChipFilterProps).onSelect?.(newValue);
  };

  const onClickChip = (event: MouseEvent) => {
    if (!disabled) {
      if (type === 'filter') {
        toggleSelect();
      } else {
        (props as ChipInputProps).onDelete?.();
      }
      event.preventDefault();
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!disabled) {
      if (type === 'filter' && (event.code === 'Space' || event.code === 'Enter')) {
        toggleSelect();
      }

      if (type === 'input' && event.code === 'Backspace') {
        (props as ChipInputProps).onDelete?.();
      }
    }
  };

  if (type === 'filter') {
    const filterClassNames = disabled
      ? 'text-secondary border-secondary'
      : 'border-secondary-mid text-secondary-deep hover:border-secondary-deep focus-visible:border-secondary-deep active:outline-pressedInner';
    return (
      <div
        role="button"
        className={cx(commonClasses, filterClassNames, { 'border-secondary-light bg-secondary-light': isSelected }, className)}
        onClick={onClickChip}
        tabIndex={chipTabIndex}
        onKeyDown={onKeyDown}
        data-testid={dataTestId}
      >
        {icon && <PhoenixIcon className="h-3 w-3" icon={getDesignSystemIcon(icon)} />}
        {!icon && checkIconWhenSelected && renderSelected()}
        {customNode}
        <span>{label}</span>
      </div>
    );
  }

  const inputClassNames = disabled
    ? 'text-secondary'
    : 'border-secondary-mid text-secondary-deep hover:bg-secondary-mid hover:border-secondary-mid focus-visible:bg-secondary-mid focus-visible:border-secondary-mid active:outline-pressed';

  return (
    <div
      onClick={onClickChip}
      className={cx(commonClasses, inputClassNames, `border-secondary-light bg-secondary-light`, className)}
      data-testid={`${label.toLowerCase().replace(/\s+/g, '')}DeleteBtn`}
      tabIndex={chipTabIndex}
      onKeyDown={onKeyDown}
    >
      {customNode}
      <span className="truncate">{label}</span>
      <svg role="button" className={cx('inline-block h-3 w-3', disabled ? 'text-secondary' : 'text-secondary-deep', deleteIcon)}>
        <use xlinkHref={deleteIcon + '#img'} />
      </svg>
    </div>
  );
};
